import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import InfoBanner from '../../../components/ui/InfoBanner'
import { useZodForm } from '../../../lib/forms/useZodForm'
import {
  NAME_MAX_LEN,
  PREFIX_MAX_LEN,
  prefixFormSchema,
  type PrefixFormValues,
} from './prefixFormSchema'
import type { Prefix } from '../types'

interface Props {
  visible: boolean
  initial: Prefix | null
  submitting: boolean
  /**
   * Kaydet devre dışı. `submitting`'den ayrıdır: etki sayısı çekilirken Kaydet kapatılır ama **İptal
   * açık kalır** (kullanıcı bekleyen bir istekte kilitlenmez).
   */
  submitDisabled?: boolean
  /** Sunucudan gelen alan-bazlı ön ek hatası (ör. yinelenen ön ek); yoksa `null`. */
  prefixError: string | null
  onSubmit: (values: PrefixFormValues) => void
  onHide: () => void
}

/**
 * Ön Ek Tanımı düzenleme penceresi (056) — **nesne adı ve ön ek**; ikisi de düzenlenebilir (FR-004).
 *
 * Pencere açıldığı andan itibaren, ön ek değişikliğinin **geriye dönük** olduğunu söyleyen bir
 * bilgilendirme bandı gösterilir (FR-010) — alanlara dokunulmadan da durur. Kaydetme sırasındaki
 * **koşullu onay adımı** çağıran sayfada kurulur (FR-010a): ön ek gerçekten değişmişse etkilenen kayıt
 * sayısıyla bir onay penceresi çıkar.
 *
 * Durum (Aktif/Pasif) burada ne gösterilir ne düzenlenir (Clarifications Q5).
 */
export default function PrefixFormDialog({
  visible,
  initial,
  submitting,
  submitDisabled = false,
  prefixError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(prefixFormSchema, {
    defaultValues: { name: '', prefix: '' },
  })

  useEffect(() => {
    if (visible) reset({ name: initial?.name ?? '', prefix: initial?.prefix ?? '' })
  }, [visible, initial, reset])

  // Sunucu tarafı alan hatası (yinelenen ön ek) RHF'e taşınır → alan altında, tek tip gösterilir.
  useEffect(() => {
    if (prefixError) setError('prefix', { message: prefixError })
  }, [prefixError, setError])

  return (
    <FormDialog
      visible={visible}
      header={t('prefixes.form.editTitle')}
      submitLabel={t('prefixes.form.save')}
      submitting={submitting}
      submitDisabled={submitting || submitDisabled}
      onSubmit={handleSubmit(onSubmit)}
      onHide={onHide}
    >
      <div className="admin-form">
        {/* FR-010: geriye dönük etki, pencere açılışından itibaren görünür. */}
        <InfoBanner
          tone="warn"
          title={t('prefixes.form.retroInfo.title')}
          description={t('prefixes.form.retroInfo.desc')}
        />
        <FormTextField
          control={control}
          name="name"
          label={t('prefixes.form.name')}
          required
          maxLength={NAME_MAX_LEN}
          autoFocus
          full
        />
        <FormTextField
          control={control}
          name="prefix"
          label={t('prefixes.form.prefix')}
          required
          maxLength={PREFIX_MAX_LEN}
        />
      </div>
    </FormDialog>
  )
}
